import { Flex, Box, Image, useDisclosure } from '@chakra-ui/core'
import { IComment, RootState } from 'constants/interfaces'
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { getCurrentEpoch } from 'utils'
import { IFetchComments } from 'hooks/useComments'
import { AnnouncementChatItem } from 'components/Chat/AnnouncementChat/AnnouncementChatItem'
import { useUniqueMessages } from 'hooks/useUniqueMessages'
import InfiniteScroll from 'react-infinite-scroll-component'
import CommentsContentLoader from 'components/Comments/CommentsContentLoader'
import { FbGridVideoPreview } from 'components/CommonComponents/FbGrid/FbGridVideoPreview'
import { PostAudioList } from 'components/NewsFeed/PostAudioList'
import { PostMediaGrid } from 'components/NewsFeed/PostMediaGrid'
import { CommentButton } from 'components/Post/CommentButton'
import { LikeButton } from 'components/Post/LikeButton'
import { LikeButtonSquare } from 'components/Post/LikeButtonSquare'
import LikesModal from 'components/Post/LikesModal'
import { renderTextWithLineBreaks } from 'components/Post/PostTitle'
import { ViewButton } from 'components/Post/ViewButton'
import { ViewsModal } from 'components/Post/ViewsModal'
import { UploadedFileList } from 'components/UploadedFileList'
import { Post } from 'redux/reducers/feed'
import { calculateUserActivity } from 'utils'
import { useDispatch, useSelector } from 'react-redux'
import { updateCommentViews, updateLikes } from 'redux/actions/comments'

interface AnnouncementCommentModalBodyProps {
  fetchRepliesLoading: boolean
  chatId: string
  fetchReplies: (body: IFetchComments) => Promise<IComment[] | undefined>
  replies:
    | {
        items: IComment[]
        hasMore: boolean
      }
    | undefined
  parentCID: string
  repliesCounter: number
  message: IComment
  chat: Post
  likes: { [key: string]: boolean }
  views: { [key: string]: boolean }
  repliesCounterLoading: boolean
  targetCommentId?: string | null
}

export const AnnouncementCommentModalBody = memo(
  ({
    replies,
    fetchReplies,
    chatId,
    fetchRepliesLoading,
    parentCID,
    repliesCounter,
    message,
    chat,
    likes,
    views,
    repliesCounterLoading,
    targetCommentId,
  }: AnnouncementCommentModalBodyProps) => {
    const dispatch = useDispatch()
    const { uid } = useSelector((state: RootState) => state.auth)
    const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onCloseViewModal } = useDisclosure()
    const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onCloseLikesModal } = useDisclosure()
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const messageContentRef = useRef<HTMLDivElement | null>(null)
    const [flexDirection, setFlexDirection] = useState<'column' | 'column-reverse'>('column-reverse')
    const [lastMessageCreatedAt, setLastMessageCreatedAt] = useState<number | null>(null)
    const uniqueMessages = useUniqueMessages(replies?.items ?? [])

    const onLikeClick = () => {
      dispatch(updateLikes(message.cid, uid!))
    }

    const likesCounter = (likes && Object.keys(likes).length) ?? 0
    const viewsCounter = (views && Object.keys(views).length) ?? 0

    const users = Array.from(new Set([...(chat?.group_users ?? []), ...(chat?.post_origin_users ?? [])]))
    const viewed = useMemo(() => calculateUserActivity(users, views), [users, views])
    const liked = useMemo(() => calculateUserActivity(users, likes), [likes, users])

    useEffect(() => {
      if (chat.created_by === uid) return
      dispatch(updateCommentViews(message.cid, uid!))
    }, [chat.created_by, dispatch, message.cid, uid])

    const getReplies = useCallback(() => {
      if (fetchRepliesLoading) return

      try {
        let timestampFromToGetComments = getCurrentEpoch()

        if (replies?.items && replies.items.length > 0) {
          timestampFromToGetComments = replies.items[0].created_at_ts
        }

        fetchReplies({
          limit: 12,
          created_ts: timestampFromToGetComments,
          parentCID: parentCID,
          isManuallyTriggered: false,
          id: chatId,
        })
      } catch (error) {
        console.log(error)
      }
    }, [chatId, fetchRepliesLoading, fetchReplies, parentCID, replies])

    // Первоначальная загрузка реплаев при монтировании
    useEffect(() => {
      const shouldFetch = !replies?.items || replies.items.length === 0
      if (shouldFetch) {
        getReplies()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current
        const shouldUseColumn = container.scrollHeight <= container.clientHeight
        setFlexDirection(shouldUseColumn ? 'column' : 'column-reverse')
      }
    }, [replies])

    useEffect(() => {
      if (replies?.items && replies.items.length > 0) {
        const lastMessage = replies.items[replies.items.length - 1]
        setLastMessageCreatedAt(lastMessage.created_at_ts)
      }
    }, [replies])

    useEffect(() => {
      if (scrollContainerRef.current && lastMessageCreatedAt) {
        const container = scrollContainerRef.current
        const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100

        if (isAtBottom) {
          container.scrollTop = container.scrollHeight
        }
      }
    }, [lastMessageCreatedAt])

    return (
      <Flex flexDir="column" h="100%" w="100%">
        <Box ref={messageContentRef} flexShrink={0} p="20px" borderBottom="1px solid #e2e8f0">
          {message && (
            <>
              {message?.message && (
                <Flex alignItems="center" mb="10px">
                  <Box wordBreak={'break-word'}>{renderTextWithLineBreaks({ text: message?.message })}</Box>
                </Flex>
              )}
              {message?.audios && message.audios.length > 0 && (
                <Box py="10px">
                  <PostAudioList audios={message.audios} />
                </Box>
              )}

              {message?.files && message.files.length > 0 && (
                <Box marginBottom="15px">
                  <UploadedFileList fileList={message.files} />
                </Box>
              )}

              {message?.images && message.images.length > 0 && (
                <Flex mb="10px" justifyContent="center">
                  <PostMediaGrid media={message.images} isEditable={false} px="0px" itemSize="150px">
                    {message.images !== null &&
                      message.images?.map((imageSrc) => (
                        <Box key={imageSrc} borderRadius="10px" overflow="hidden" width="100%" height="100%">
                          <Image src={imageSrc} width="100%" height="100%" objectFit="cover" />
                        </Box>
                      ))}
                  </PostMediaGrid>
                </Flex>
              )}

              {message.videos && message.videos.length > 0 && (
                <Flex mb="10px" justifyContent="center">
                  <PostMediaGrid media={message.videos} isEditable={false} itemSize="150px">
                    {message.videos !== null &&
                      message.videos?.map((videoSrc) => (
                        <Box key={videoSrc} borderRadius="10px" overflow="hidden" width="100%" height="100%">
                          <FbGridVideoPreview src={videoSrc} />
                        </Box>
                      ))}
                  </PostMediaGrid>
                </Flex>
              )}
            </>
          )}
          <Flex flexDir="column" justifyContent="center">
            <Flex w="100%" justifyContent="flex-end">
              <LikeButton
                created_by={message.created_by}
                likes_counter={likesCounter}
                liked={liked}
                isShowLikeButton={true}
                isLikedPost={likes?.[uid!] ?? false}
                isDoublePost={false}
                isAllPostCollapsed={true}
                handleLikesModal={onLikesModalOpen}
              />

              <ViewButton
                viewed={viewed}
                views_counter={viewsCounter}
                created_by={chat.created_by}
                isShowViewIcon={true}
                isViewed={views?.[uid!] ?? false}
                isDoublePost={false}
                isAllPostCollapsed={true}
                openModal={onViewModalOpen}
              />

              <CommentButton
                postId={chat.post_id}
                commentsCounterLoading={repliesCounterLoading}
                showCommentsSection={false}
                commentsCounter={repliesCounter}
                isViewed={true}
                isDoublePost={false}
                isAllPostCollapsed={true}
              />
            </Flex>
            {message.created_by !== uid && (
              <LikeButtonSquare liked={likes?.[uid!] ?? false} likeHandler={onLikeClick} />
            )}
          </Flex>
        </Box>

        {/* Скроллируемая секция с реплаями */}
        <Flex
          id="announcementCommentModalScrollContainer"
          ref={scrollContainerRef}
          flexDir={flexDirection}
          flex="1"
          minH="0"
          w="100%"
          overflowY="auto"
          p="20px"
          dir="ltr"
        >
          <InfiniteScroll
            dataLength={replies?.items.length ?? 0}
            next={getReplies}
            style={{ display: 'flex', flexDirection: 'column-reverse' }}
            inverse={true}
            hasMore={replies?.hasMore || false}
            loader={null}
            scrollableTarget="announcementCommentModalScrollContainer"
          >
            {uniqueMessages?.map((reply: IComment) => (
              <AnnouncementChatItem key={reply.cid} message={reply} isTarget={targetCommentId === reply.cid} />
            ))}
            {fetchRepliesLoading && <CommentsContentLoader count={uniqueMessages.length ? 12 : undefined} />}
          </InfiniteScroll>
        </Flex>

        {isViewModalOpen && (
          <ViewsModal
            isOpen={isViewModalOpen}
            onClose={onCloseViewModal}
            views_counter={viewsCounter}
            created_by={message.created_by}
            viewed={viewed}
          />
        )}
        {isLikesModalOpen && (
          <LikesModal
            isOpen={isLikesModalOpen}
            onClose={onCloseLikesModal}
            likes_counter={likesCounter}
            created_by={message.created_by}
            liked={liked}
            isShowPings={false}
          />
        )}
      </Flex>
    )
  }
)
